/**
 * Shared measurement units + physical scale resolution for AnnotationUIs.
 *
 * Design:
 * - Canonical physical scale is stored/resolved as **µm per pixel** (umPerPixel).
 * - UI can choose display units (µm, mm, cm). The toolkit receives:
 *     unitsPerPixelInDisplayUnit + labelUnitInDisplayUnit
 * - Pixel mode is always 1 px/px with label "px".
 *
 * This module is measurement-type agnostic (usable for linear, area, etc.).
 */
import api from '../../API';

export const MEASUREMENT_ROUNDING_MODE = {
    ROUND: 'round',
    TRUNCATE: 'truncate',
};

export const MEASUREMENT_SPACE = {
    PIXEL: 'pixel',
    PHYSICAL: 'physical',
};

export const PHYSICAL_SCALE_SOURCE = {
    AUTO: 'auto',
    MANUAL: 'manual',
};

export const DISPLAY_UNIT = {
    UM: 'um',
    MM: 'mm',
    CM: 'cm',
};

/**
 * Scale resolution lifecycle for gating UIs (no timing-based px while tiles load).
 * `pending` is used only by hooks before the async tiles request completes.
 */
export const MEASUREMENT_SCALE_STATUS = {
    PENDING: 'pending',
    RESOLVED_PIXEL: 'resolved_pixel',
    RESOLVED_PHYSICAL: 'resolved_physical',
    RESOLVED_FALLBACK_NO_MM_X: 'resolved_fallback_no_mm_x',
    RESOLVED_FALLBACK_REQUEST_FAILED: 'resolved_fallback_request_failed',
    RESOLVED_FALLBACK_NO_ITEM: 'resolved_fallback_no_item',
    RESOLVED_FALLBACK_INVALID_MANUAL: 'resolved_fallback_invalid_manual',
};

export function isMeasurementScaleResolved(status) {
    return Boolean(status) && status !== MEASUREMENT_SCALE_STATUS.PENDING;
}

export function displayUnitToLabel(unit) {
    if (unit === DISPLAY_UNIT.MM) return 'mm';
    if (unit === DISPLAY_UNIT.CM) return 'cm';
    return 'µm';
}

function coerceTrimmedString(v) {
    return typeof v === 'string' ? v.trim() : '';
}

function toPositiveNumber(v) {
    const n = Number(v);
    if (!Number.isFinite(n) || n <= 0) return null;
    return n;
}

function toNonNegativeInt(v, fallback = 2) {
    const n = Number(v);
    if (!Number.isFinite(n)) return fallback;
    const i = Math.trunc(n);
    return i >= 0 ? i : fallback;
}

function normalizeRoundingMode(v) {
    return v === MEASUREMENT_ROUNDING_MODE.TRUNCATE
        ? MEASUREMENT_ROUNDING_MODE.TRUNCATE
        : MEASUREMENT_ROUNDING_MODE.ROUND;
}

function normalizeDisplayUnit(raw) {
    const s = coerceTrimmedString(raw);
    if (s === DISPLAY_UNIT.MM) return DISPLAY_UNIT.MM;
    if (s === DISPLAY_UNIT.CM) return DISPLAY_UNIT.CM;
    return DISPLAY_UNIT.UM;
}

function displayUnitFromLegacyLabel(rawUnitLabel) {
    const s = coerceTrimmedString(rawUnitLabel).toLowerCase();
    if (!s) return null;
    if (s === 'um' || s === 'µm' || s === 'micrometer' || s === 'micrometers') return DISPLAY_UNIT.UM;
    if (s === 'mm' || s === 'millimeter' || s === 'millimeters') return DISPLAY_UNIT.MM;
    if (s === 'cm' || s === 'centimeter' || s === 'centimeters') return DISPLAY_UNIT.CM;
    return null;
}

export const DEFAULT_MEASUREMENT_SCALE_CONFIG = {
    space: MEASUREMENT_SPACE.PHYSICAL,
    physical: {
        scaleSource: PHYSICAL_SCALE_SOURCE.AUTO,
        displayUnit: DISPLAY_UNIT.UM,
        manual: {
            unitsPerPixelInDisplayUnit: null,
        },
    },
    decimals: 2,
    roundingMode: MEASUREMENT_ROUNDING_MODE.ROUND,
};

/**
 * Normalize physical scale config. Accepts both the new config shape and the legacy
 * { mode:'px'|'auto'|'manual', unit, unitsPerPixel } shape.
 *
 * @param {object} physicalScaleConfig
 */
export function normalizeMeasurementScaleConfig(physicalScaleConfig) {
    const raw = physicalScaleConfig && typeof physicalScaleConfig === 'object'
        ? physicalScaleConfig
        : {};

    // Legacy shape (from older UIs)
    const legacyMode = coerceTrimmedString(raw.mode);
    if (legacyMode) {
        if (legacyMode === 'px') {
            return {
                ...DEFAULT_MEASUREMENT_SCALE_CONFIG,
                space: MEASUREMENT_SPACE.PIXEL,
                decimals: toNonNegativeInt(raw.decimals, DEFAULT_MEASUREMENT_SCALE_CONFIG.decimals),
                roundingMode: normalizeRoundingMode(raw.roundingMode),
            };
        }
        if (legacyMode === 'auto') {
            return {
                ...DEFAULT_MEASUREMENT_SCALE_CONFIG,
                space: MEASUREMENT_SPACE.PHYSICAL,
                physical: {
                    ...DEFAULT_MEASUREMENT_SCALE_CONFIG.physical,
                    scaleSource: PHYSICAL_SCALE_SOURCE.AUTO,
                },
                decimals: toNonNegativeInt(raw.decimals, DEFAULT_MEASUREMENT_SCALE_CONFIG.decimals),
                roundingMode: normalizeRoundingMode(raw.roundingMode),
            };
        }
        if (legacyMode === 'manual') {
            const displayUnit = displayUnitFromLegacyLabel(raw.unit) ?? DISPLAY_UNIT.UM;
            return {
                ...DEFAULT_MEASUREMENT_SCALE_CONFIG,
                space: MEASUREMENT_SPACE.PHYSICAL,
                physical: {
                    scaleSource: PHYSICAL_SCALE_SOURCE.MANUAL,
                    displayUnit,
                    manual: {
                        unitsPerPixelInDisplayUnit: toPositiveNumber(raw.unitsPerPixel),
                    },
                },
                decimals: toNonNegativeInt(raw.decimals, DEFAULT_MEASUREMENT_SCALE_CONFIG.decimals),
                roundingMode: normalizeRoundingMode(raw.roundingMode),
            };
        }
    }

    // New shape
    const spaceRaw = coerceTrimmedString(raw.space);
    const space = spaceRaw === MEASUREMENT_SPACE.PIXEL ? MEASUREMENT_SPACE.PIXEL : MEASUREMENT_SPACE.PHYSICAL;

    if (space === MEASUREMENT_SPACE.PIXEL) {
        return {
            ...DEFAULT_MEASUREMENT_SCALE_CONFIG,
            space: MEASUREMENT_SPACE.PIXEL,
            decimals: toNonNegativeInt(raw.decimals, DEFAULT_MEASUREMENT_SCALE_CONFIG.decimals),
            roundingMode: normalizeRoundingMode(raw.roundingMode),
        };
    }

    const physicalRaw = raw.physical && typeof raw.physical === 'object' ? raw.physical : {};
    const scaleSourceRaw = coerceTrimmedString(physicalRaw.scaleSource);
    const scaleSource = scaleSourceRaw === PHYSICAL_SCALE_SOURCE.MANUAL
        ? PHYSICAL_SCALE_SOURCE.MANUAL
        : PHYSICAL_SCALE_SOURCE.AUTO;
    const displayUnit = normalizeDisplayUnit(physicalRaw.displayUnit);

    const manualRaw = physicalRaw.manual && typeof physicalRaw.manual === 'object' ? physicalRaw.manual : {};
    const manualUnits = toPositiveNumber(manualRaw.unitsPerPixelInDisplayUnit);

    return {
        ...DEFAULT_MEASUREMENT_SCALE_CONFIG,
        ...raw,
        space: MEASUREMENT_SPACE.PHYSICAL,
        physical: {
            scaleSource,
            displayUnit,
            manual: { unitsPerPixelInDisplayUnit: manualUnits },
        },
        decimals: toNonNegativeInt(raw.decimals, DEFAULT_MEASUREMENT_SCALE_CONFIG.decimals),
        roundingMode: normalizeRoundingMode(raw.roundingMode),
    };
}

function umPerUnit(displayUnit) {
    if (displayUnit === DISPLAY_UNIT.MM) return 1000;
    if (displayUnit === DISPLAY_UNIT.CM) return 10000;
    return 1;
}

/**
 * Convert canonical µm/px scale into the display unit's units/px.
 * @param {number} umPerPixel
 * @param {'um'|'mm'|'cm'} displayUnit
 */
export function convertUmPerPixelToDisplayUnitsPerPixel(umPerPixel, displayUnit) {
    const denom = umPerUnit(displayUnit);
    return umPerPixel / denom;
}

/**
 * Convert display units/px to canonical µm/px.
 * @param {number} unitsPerPixelInDisplayUnit
 * @param {'um'|'mm'|'cm'} displayUnit
 */
export function convertDisplayUnitsPerPixelToUmPerPixel(unitsPerPixelInDisplayUnit, displayUnit) {
    const mult = umPerUnit(displayUnit);
    return unitsPerPixelInDisplayUnit * mult;
}

/**
 * Synchronous scale resolution (no tiles fetch). Same contract as resolveMeasurementScale
 * for configs that do not require async metadata.
 *
 * @param {object} physicalScaleConfig - raw or normalized config
 * @param {string|null} itemId
 * @returns {{ kind: 'pixel'|'physical', status: string, unitsPerPixel: number, unitLabel: string, umPerPixel?: number, error?: string }}
 */
export function resolveMeasurementScaleSync(physicalScaleConfig, itemId = null) {
    const cfg = normalizeMeasurementScaleConfig(physicalScaleConfig);

    if (cfg.space === MEASUREMENT_SPACE.PIXEL) {
        return {
            kind: 'pixel',
            status: MEASUREMENT_SCALE_STATUS.RESOLVED_PIXEL,
            unitsPerPixel: 1,
            unitLabel: 'px',
        };
    }

    const displayUnit = cfg.physical.displayUnit;
    const label = displayUnitToLabel(displayUnit);

    if (cfg.physical.scaleSource === PHYSICAL_SCALE_SOURCE.MANUAL) {
        const u = cfg.physical.manual?.unitsPerPixelInDisplayUnit;
        if (u != null && Number.isFinite(u) && u > 0) {
            const umPerPixel = convertDisplayUnitsPerPixelToUmPerPixel(u, displayUnit);
            return {
                kind: 'physical',
                status: MEASUREMENT_SCALE_STATUS.RESOLVED_PHYSICAL,
                umPerPixel,
                unitsPerPixel: u,
                unitLabel: label,
            };
        }
        return {
            kind: 'pixel',
            status: MEASUREMENT_SCALE_STATUS.RESOLVED_FALLBACK_INVALID_MANUAL,
            unitsPerPixel: 1,
            unitLabel: 'px',
            error: 'Manual scale is invalid.',
        };
    }

    // Auto: need async tiles unless no item (resolved fallback immediately)
    if (!itemId) {
        return {
            kind: 'pixel',
            status: MEASUREMENT_SCALE_STATUS.RESOLVED_FALLBACK_NO_ITEM,
            unitsPerPixel: 1,
            unitLabel: 'px',
            error: 'Auto scale unavailable: no item selected.',
        };
    }

    return null;
}

/**
 * Resolve scale for the current item and normalized config.
 *
 * Returns a stable shape consumable by any UI:
 * - kind + status distinguish explicit pixel mode vs intentional fallbacks vs physical.
 * - `unitsPerPixel` + `unitLabel` are in the chosen display unit when physical; else px.
 *
 * Never returns status `pending` (that is hook-only while awaiting this promise).
 */
export async function resolveMeasurementScale(itemId, physicalScaleConfig) {
    const sync = resolveMeasurementScaleSync(physicalScaleConfig, itemId);
    if (sync != null) {
        return sync;
    }

    const cfg = normalizeMeasurementScaleConfig(physicalScaleConfig);
    const displayUnit = cfg.physical.displayUnit;
    const label = displayUnitToLabel(displayUnit);

    try {
        const res = await api.get(`item/${itemId}/tiles`);
        const mmX = res.data?.mm_x;
        if (mmX != null && mmX > 0) {
            const umPerPixel = mmX * 1000; // mm → µm
            const unitsPerPixel = convertUmPerPixelToDisplayUnitsPerPixel(umPerPixel, displayUnit);
            return {
                kind: 'physical',
                status: MEASUREMENT_SCALE_STATUS.RESOLVED_PHYSICAL,
                umPerPixel,
                unitsPerPixel,
                unitLabel: label,
            };
        }
        return {
            kind: 'pixel',
            status: MEASUREMENT_SCALE_STATUS.RESOLVED_FALLBACK_NO_MM_X,
            unitsPerPixel: 1,
            unitLabel: 'px',
            error: 'Auto scale unavailable: slide metadata does not include a valid mm_x.',
        };
    } catch (err) {
        return {
            kind: 'pixel',
            status: MEASUREMENT_SCALE_STATUS.RESOLVED_FALLBACK_REQUEST_FAILED,
            unitsPerPixel: 1,
            unitLabel: 'px',
            error: err?.message || 'Auto scale unavailable: metadata request failed.',
        };
    }
}

/**
 * Structural validation for measurement scale config (new + legacy).
 * @returns {{code: string, message: string}[]}
 */
export function validateMeasurementScaleConfig(physicalScaleConfig) {
    const issues = [];
    const cfg = normalizeMeasurementScaleConfig(physicalScaleConfig);

    if (cfg.space === MEASUREMENT_SPACE.PHYSICAL && cfg.physical.scaleSource === PHYSICAL_SCALE_SOURCE.MANUAL) {
        const u = cfg.physical.manual?.unitsPerPixelInDisplayUnit;
        if (u == null || !Number.isFinite(u) || u <= 0) {
            issues.push({
                code: 'invalid_manual_scale',
                message: 'Manual physical scale requires a positive "units per pixel" value.',
            });
        }
    }

    if (cfg.decimals != null) {
        const n = Number(cfg.decimals);
        if (!Number.isFinite(n) || Math.trunc(n) < 0) {
            issues.push({
                code: 'invalid_precision_decimals',
                message: 'Decimal places must be an integer greater than or equal to 0.',
            });
        }
    }
    if (cfg.roundingMode && !Object.values(MEASUREMENT_ROUNDING_MODE).includes(cfg.roundingMode)) {
        issues.push({
            code: 'invalid_precision_rounding_mode',
            message: `Rounding mode "${String(cfg.roundingMode)}" is invalid. Use "round" or "truncate".`,
        });
    }

    return issues;
}

